"use client";

import {JSX, useState} from "react";

import SmartImage from "@/shared/ui/smart-image";

import type {TripUnitPmsPropertyManagement} from "@/entities/trip/api/types";

import {useDetectClickOutside} from "@/shared/hooks/dom/use-detect-click-outside";
import {useTranslation} from "@/shared/i18n";
import {Typography} from "@/shared/ui/data-display/typography";
import {FloatingDropdown} from "@/shared/ui/overlay/floating-dropdown";
import {assetPath} from "@/shared/utils/asset-path";

import "./styles/trip-help-panel.scss";

export interface TripHelpButtonProps {
    support: TripUnitPmsPropertyManagement;
}

export function TripHelpButton({support}: TripHelpButtonProps): JSX.Element {
    const {t} = useTranslation();
    const [isOpen, setIsOpen] = useState(false);

    const popoverRef = useDetectClickOutside<HTMLDivElement>({
        onTriggered: () => setIsOpen(false),
    });

    return (
        <div className="trip-help-button" ref={popoverRef}>
            <FloatingDropdown
                isOpen={isOpen}
                onOpenChange={setIsOpen}
                placement="bottom-end"
                offsetValue={8}
                dropdownClassName="trip-help-popover"
                trigger={
                    <button type="button" className="trip-help-button__trigger" onClick={() => setIsOpen((open) => !open)}>
                        <SmartImage src={assetPath("/images/icons/message-question.svg")} alt="" width={18} height={18} />
                        {t("trip.helpButton")}
                    </button>
                }
            >
                <div className="trip-help-popover__content">
                    <Typography as="p" size={14} weight={700}>
                        {support.name}
                    </Typography>
                    <Typography as="p" size={12} color="paragraph" className="trip-help-popover__description">
                        {t("trip.needHelpDescription", {name: support.name})}
                    </Typography>

                    {support.email && (
                        <a href={`mailto:${support.email}`} className="trip-help-popover__row">
                            <SmartImage src={assetPath("/images/icons/email.svg")} alt="" width={16} height={16} />
                            <span>{support.email}</span>
                        </a>
                    )}
                    {support.phone && (
                        <a href={`tel:${support.phone}`} className="trip-help-popover__row">
                            <SmartImage src={assetPath("/images/icons/phone.svg")} alt="" width={16} height={16} />
                            <span>{support.phone}</span>
                        </a>
                    )}
                </div>
            </FloatingDropdown>
        </div>
    );
}
